import { useCallback, useEffect, useMemo, useState } from "react"
import { GetApp } from "../App"
import { GetShip, GetShipContext } from "../App/Context"
import { Loading } from "../Components/Loading"
import { nestedNoteRows, useDataStore, useQuery, useQueryReady, useRetainedEntities } from "../Data"
import { CruiseApi, ICruise } from "../Types"
import { Subscription } from "../Types/Generated/LogbookApi/Subscriptions"
import { SchemaEntities } from "../Types/Generated/LogbookModel/Schema"
import { GetShipView, ShipRoutes } from "./ShipView"
import { ShipInfoHeaderView } from "./ShipInfoHeaderView"
import { CruiseCalendarViewData } from "./CruiseCalendar/Context"
import { CruiseCalendarViewProps } from "./CruiseCalendar/Params"
import { CruiseCalendarGridView } from "./CruiseCalendar/GridView"
import { CruiseCalendarEmptyView } from "./CruiseCalendar/EmptyView"
import { useAuthoritativeSeedIds } from "./useAuthoritativeSeedIds"

interface CruiseCalendarContentProps {
  cruises: readonly ICruise[] | undefined;
}

function CruiseCalendarContent({ cruises }: CruiseCalendarContentProps) {
  const app = GetApp()!;
  const { navigateShip } = GetShipView();
  const selectCruise = useCallback((cruiseId: number) => {
    navigateShip(ShipRoutes.cruise, { cruiseId: cruiseId });
  }, []);

  const onFocus = useCallback(() => {
    navigateShip(ShipRoutes.focus);
  }, []);

  const today = new Date().toISOString();
  const sortedCruises = useMemo(() => CruiseCalendarViewData.sortCruises(cruises ? [...cruises] : undefined, app.cloudSession()), [cruises]);
  const minMaxYears = useMemo(() => CruiseCalendarViewData.yearRange(sortedCruises, today), [sortedCruises]);

  if (!cruises || (cruises.length == 0 && !app.cloudSession())) {
    return <Loading />;
  }

  if (cruises.length < 1 && app.cloudSession()) {
    return (
      <>
        <ShipInfoHeaderView actions={[]} />
        <CruiseCalendarEmptyView />
      </>
    );
  }

  return (
    <>
      <ShipInfoHeaderView actions={[]} />
      <CruiseCalendarGridView sortedCruises={sortedCruises} minMaxYears={minMaxYears} selectCruise={selectCruise} onFocus={onFocus} />
    </>
  );
}

function useCruiseCalendarAccess() {
  const app = GetApp();
  const ship = GetShip();
  const { apiUser } = GetShipContext();
  const { navigateShip } = GetShipView();
  const levelReady = apiUser?.shipLevel(ship.id) !== undefined;
  const available = levelReady &&
    new CruiseApi(app.apiClient).cruises.list({ params: { shipId: ship.id }, query: {} }).available(apiUser);

  useEffect(() => {
    if (!levelReady || available) {
      return;
    }
    if (!app.cloudSession()) {
      navigateShip(ShipRoutes.focus);
    }
  }, [app, available, levelReady, navigateShip]);

  return available;
}

export function CruiseCalendarView({ initCruises }: CruiseCalendarViewProps) {
  const app = GetApp();
  const ship = GetShip();
  const { navigateShip } = GetShipView();
  const available = useCruiseCalendarAccess();
  const store = useDataStore();
  const storedCruises = useQuery(store, SchemaEntities.Cruise, { shipId: ship.id });
  const seededIds = useAuthoritativeSeedIds(
    initCruises,
    Subscription.Cruise,
    (cruise) => cruise.shipId === ship.id,
  );
  const ready = useQueryReady(
    store,
    SchemaEntities.Cruise,
    { shipId: ship.id },
    available && initCruises === undefined,
    initCruises !== undefined,
  );
  useRetainedEntities(
    [SchemaEntities.Cruise, SchemaEntities.Note, SchemaEntities.NoteAttachment, SchemaEntities.StoredCalculation],
    available,
    ship.id,
  );

  useState(() => {
    if (initCruises) {
      const notes = initCruises.flatMap((cruise) => cruise.notes ?? []);
      store.applyBundle({
        entities: { Cruise: initCruises, ...nestedNoteRows(notes) },
        authoritative: { Cruise: { shipId: ship.id } },
      });
    }
    return true;
  });

  useEffect(() => {
    if (ready && storedCruises.length === 0 && app.cloudSession()) {
      navigateShip(ShipRoutes.calendar);
    }
  }, [app, navigateShip, ready, storedCruises.length]);

  const cruises = initCruises !== undefined && storedCruises.length === 0
    ? initCruises
    : ready
      ? (seededIds ? storedCruises.filter((cruise) => seededIds.has(cruise.id)) : storedCruises)
      : undefined;
  return <CruiseCalendarContent cruises={cruises} />;
}

export { CruiseCalendarViewData } from "./CruiseCalendar/Context"
export type { CruiseCalendarViewProps } from "./CruiseCalendar/Params"
